import { type ReactNode, useEffect } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ConsentBanner, shouldAskHere } from './analytics/ConsentBanner.js';
import { useAnalytics } from './analytics/useAnalytics.js';
import { AppHeader } from './components/AppHeader.js';
import { AppVersion } from './components/AppVersion.js';
import { LegalLinks } from './components/LegalLinks.js';
import { RequireAuth } from './components/RequireAuth.js';
import { TourOverlay } from './components/tour/TourOverlay.js';
import { AuthProvider, useAuth } from './lib/auth.js';
import { isGuideSurface } from './lib/tour/steps.js';
import { startEventFlushing } from './lib/track.js';
import { COMPARISONS, USE_CASES } from './seo/marketingContent.js';
import { useNoIndex } from './lib/useNoIndex.js';
import { TourProvider, useTour } from './lib/tour/TourProvider.js';
import { WorkspaceProvider } from './lib/workspace.js';
import { Usage } from './pages/admin/Usage.js';
import { ForgotPassword } from './pages/auth/ForgotPassword.js';
import { GoogleCallback } from './pages/auth/GoogleCallback.js';
import { ResetPassword } from './pages/auth/ResetPassword.js';
import { SignIn } from './pages/auth/SignIn.js';
import { SignUp } from './pages/auth/SignUp.js';
import { VerifyEmail } from './pages/auth/VerifyEmail.js';
import { Account } from './pages/account/Account.js';
import { Gallery } from './pages/dev/Gallery.js';
import { AcceptInvite } from './pages/invite/AcceptInvite.js';
import { Landing } from './pages/landing/Landing.js';
import { Comparison } from './pages/marketing/Comparison.js';
import { UseCase } from './pages/marketing/UseCase.js';
import { Privacy } from './pages/legal/Privacy.js';
import { Terms } from './pages/legal/Terms.js';
import { Unsubscribe } from './pages/lifecycle/Unsubscribe.js';
import { BigScreen } from './pages/live/BigScreen.js';
import { Join } from './pages/live/Join.js';
import { Presenter } from './pages/live/Presenter.js';
import { QuizEditor } from './pages/quizzes/QuizEditor.js';
import { QuizList } from './pages/quizzes/QuizList.js';
import { QuizResultsPage } from './pages/quizzes/QuizResults.js';
import { RunResultsPage } from './pages/quizzes/RunResults.js';
import { Members } from './pages/workspace/Members.js';
import { Settings } from './pages/workspace/Settings.js';

function NotFound() {
  // CloudFront answers an unknown path with this page and a 200, so without
  // the tag every URL anyone can invent is an indexable near-duplicate.
  useNoIndex();
  return (
    <div className="center-page">
      <div className="card">
        <h1>Page not found</h1>
        <p className="muted">
          Check the address, or head back to <Link to="/polls">your polls</Link>.
        </p>
      </div>
    </div>
  );
}

/** Old bookmarks and sign-in redirects predate the quiz → poll rename. */
function LegacyQuizRedirect() {
  const location = useLocation();
  const to = location.pathname.replace(/^\/quizzes/, '/polls') + location.search;
  return <Navigate to={to} replace />;
}

/** Signed-in users have no reason to see sign-in or sign-up again. */
function GuestOnly({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === 'signed-in') return <Navigate to="/polls" replace />;
  return <>{children}</>;
}

/**
 * Lets someone who skipped the guide, or wants it again, start it.
 *
 * Absent where the guide will not draw — the presenter controls are a console
 * route and get this footer, so without the check it would be a button that
 * does nothing, and the run would then surface unannounced on the next page.
 */
function ReplayGuide() {
  const { start } = useTour();
  const { pathname } = useLocation();
  if (!isGuideSurface(pathname)) return null;
  return (
    <button type="button" className="tour-skip" onClick={start}>
      Guide
    </button>
  );
}

function Console({ children }: { children: ReactNode }) {
  return (
    <RequireAuth>
      <AppHeader />
      <main className="app-main">{children}</main>
      <footer className="app-footer">
        <ReplayGuide />
        <LegalLinks />
        <AppVersion />
      </footer>
    </RequireAuth>
  );
}

/**
 * Page-view counting, and the consent prompt everywhere but the audience.
 *
 * Inside the router because it reads the location; `startEventFlushing` stays
 * in App itself, which is outside it. Suppressed on the two audience surfaces:
 * the projector has nobody to ask, and a phone that has just scanned a code is
 * mid-task — Join puts the prompt on the ended screen instead, which is the
 * one moment in an audience's visit when interrupting costs nothing.
 */
function Analytics() {
  useAnalytics();
  const { pathname } = useLocation();
  const { phase } = useTour();
  // Counting starts regardless; shouldAskHere decides only when to ask.
  return shouldAskHere(pathname, phase) ? <ConsentBanner /> : null;
}

export function App() {
  // Mounted at the root, not per console route: a buffered count taken on the
  // poll list must still flush after navigating to the builder, and a listener
  // that unmounts with the page it was registered on would drop it.
  useEffect(startEventFlushing, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <WorkspaceProvider>
          {/* Above <Routes> on purpose: each console route renders its own
              <Console>, so a run parked inside one would be discarded the
              moment creating a poll navigates to the builder. */}
          <TourProvider>
            <div className="app-shell">
              <Routes>
                <Route path="/" element={<Landing />} />

                {/* Public marketing pages. Each one is prerendered with its own
                  head at build time and listed in the sitemap; the route table
                  in seo/routes.ts is what keeps those three in step. Outside
                  GuestOnly on purpose — a signed-in user following a link to a
                  comparison page should see the page, not a redirect. */}
                {COMPARISONS.map((page) => (
                  <Route key={page.path} path={page.path} element={<Comparison page={page} />} />
                ))}
                {USE_CASES.map((page) => (
                  <Route key={page.path} path={page.path} element={<UseCase page={page} />} />
                ))}

                {/* Outside every gate: reachable signed-out, signed-in, and from
                  inside the terms-acceptance screen. */}
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />

                {/* Followed from a mail client with no session, and called
                  directly by mailbox providers honouring List-Unsubscribe-Post.
                  The token in the URL is the whole capability. */}
                <Route path="/unsubscribe" element={<Unsubscribe />} />

                <Route
                  path="/signin"
                  element={
                    <GuestOnly>
                      <SignIn />
                    </GuestOnly>
                  }
                />
                <Route
                  path="/signup"
                  element={
                    <GuestOnly>
                      <SignUp />
                    </GuestOnly>
                  }
                />
                <Route path="/verify" element={<VerifyEmail />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/auth/callback" element={<GoogleCallback />} />
                {/* Reachable signed-out: the invitee may not have an account yet. */}
                <Route path="/invite/:token" element={<AcceptInvite />} />

                <Route
                  path="/polls"
                  element={
                    <Console>
                      <QuizList />
                    </Console>
                  }
                />
                <Route
                  path="/polls/:quizId"
                  element={
                    <Console>
                      <QuizEditor />
                    </Console>
                  }
                />
                <Route
                  path="/polls/:quizId/results"
                  element={
                    <Console>
                      <QuizResultsPage />
                    </Console>
                  }
                />
                {/* No quizId in the path: a sealed run outlives its poll (#46). */}
                <Route
                  path="/runs/:sessionId"
                  element={
                    <Console>
                      <RunResultsPage />
                    </Console>
                  }
                />
                <Route
                  path="/people"
                  element={
                    <Console>
                      <Members />
                    </Console>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <Console>
                      <Settings />
                    </Console>
                  }
                />
                {/* Person-scoped, so no capability gate: every signed-in
                    account has one, whatever its role in a workspace. */}
                <Route
                  path="/account"
                  element={
                    <Console>
                      <Account />
                    </Console>
                  }
                />
                <Route
                  path="/usage"
                  element={
                    <Console>
                      <Usage />
                    </Console>
                  }
                />

                <Route
                  path="/polls/:quizId/present"
                  element={
                    <Console>
                      <Presenter />
                    </Console>
                  }
                />
                <Route path="/quizzes/*" element={<LegacyQuizRedirect />} />

                {/* Audience-facing routes stay outside the auth gate by design. */}
                <Route path="/j" element={<Join />} />
                <Route path="/j/:code" element={<Join />} />
                <Route path="/screen/:quizId" element={<BigScreen />} />

                {/* Style gallery, stripped from production builds. */}
                {import.meta.env.DEV ? <Route path="/dev/gallery" element={<Gallery />} /> : null}

                <Route path="*" element={<NotFound />} />
              </Routes>
            </div>
            <TourOverlay />
            <Analytics />
          </TourProvider>
        </WorkspaceProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
